// MUI's own text in the selected language (TGCLOUD-1893): the data grid's
// footer ("Rows per page"), column menu, filter panel and toolbar; pagination;
// autocomplete ("No options"); alert and picker buttons. MUI ships it in
// English inside each component, and nothing here localised it, so every grid
// footer stayed English whatever the language.
//
// Each English default goes through the app's own t(), so the text is
// translated the way the rest of the UI is, in every language the menu
// offers, and shows in the pseudo-locale. An entry MUI builds with a function
// is rebuilt here as one interpolated sentence. English gets no overrides, so
// English is exactly MUI's own.

import { enUS as gridEnUS } from '@mui/x-data-grid-premium/locales';
import { enUS as pickersEnUS } from '@mui/x-date-pickers/locales';
import { intlLocale, isEnglish } from './language';

const GRID_ENGLISH = gridEnUS.components.MuiDataGrid.defaultProps.localeText;
const PICKERS_ENGLISH =
  pickersEnUS.components.MuiLocalizationProvider.defaultProps.localeText;

/** Every string value of `english` through t(). Functions are left out. */
const translateStrings = (english, t) => {
  const out = {};
  for (const [key, value] of Object.entries(english)) {
    if (typeof value === 'string') out[key] = t(value);
  }
  return out;
};

const pageAriaLabel = (t) => (type) => {
  if (type === 'first') return t('Go to first page');
  if (type === 'last') return t('Go to last page');
  if (type === 'next') return t('Go to next page');
  return t('Go to previous page');
};

/**
 * The data grid's localeText in the current language: its strings through
 * t(), and the function entries a user meets rebuilt as sentences. The rest
 * (chart and AI-prompt builders) keep MUI's English.
 */
export const gridLocaleText = (t, lang) => {
  const number = (n) => Number(n).toLocaleString(intlLocale(lang));
  const activeFilters = (count) =>
    count !== 1
      ? t('{{count}} active filters', { count })
      : t('{{count}} active filter', { count });
  return {
    ...translateStrings(GRID_ENGLISH, t),
    toolbarFiltersTooltipActive: activeFilters,
    columnHeaderFiltersTooltipActive: activeFilters,
    columnMenuAriaLabel: (columnName) =>
      t('{{columnName}} column menu', { columnName }),
    footerRowSelected: (count) =>
      count !== 1
        ? t('{{count}} rows selected', { count: number(count) })
        : t('{{count}} row selected', { count: number(count) }),
    footerTotalVisibleRows: (visibleCount, totalCount) =>
      t('{{visibleCount}} of {{totalCount}}', {
        visibleCount: number(visibleCount),
        totalCount: number(totalCount)
      }),
    groupColumn: (name) => t('Group by {{name}}', { name }),
    unGroupColumn: (name) => t('Stop grouping by {{name}}', { name }),
    paginationDisplayedRows: ({ from, to, count, estimated }) => {
      const range = { from: number(from), to: number(to) };
      const unknownRowCount = count == null || count === -1;
      if (!unknownRowCount)
        return t('{{from}}–{{to}} of {{count}}', {
          ...range,
          count: number(count)
        });
      if (estimated && estimated > to)
        return t('{{from}}–{{to}} of around {{estimated}}', {
          ...range,
          estimated: number(estimated)
        });
      return t('{{from}}–{{to}} of more than {{to}}', range);
    },
    paginationItemAriaLabel: pageAriaLabel(t)
  };
};

const pickersLocaleText = (t) => ({
  ...translateStrings(PICKERS_ENGLISH, t),
  calendarViewSwitchingButtonAriaLabel: (view) =>
    view === 'year'
      ? t('year view is open, switch to calendar view')
      : t('calendar view is open, switch to year view'),
  openDatePickerDialogue: (formattedDate) =>
    formattedDate
      ? t('Choose date, selected date is {{date}}', { date: formattedDate })
      : t('Choose date'),
  openTimePickerDialogue: (formattedTime) =>
    formattedTime
      ? t('Choose time, selected time is {{time}}', { time: formattedTime })
      : t('Choose time'),
  calendarWeekNumberAriaLabelText: (weekNumber) =>
    t('Week {{weekNumber}}', { weekNumber })
});

/**
 * Theme `components` entries that put MUI's text in the current language, or
 * null for English. `signature` changes only when one of the texts does, so a
 * theme built from it is rebuilt only then, not on every translation that
 * arrives for some other screen.
 *
 * @returns {{ components: object, signature: string } | null}
 */
export const muiLocaleComponents = (t, lang) => {
  if (isEnglish(lang)) return null;
  const number = (n) => Number(n).toLocaleString(intlLocale(lang));
  const components = {
    MuiBreadcrumbs: { defaultProps: { expandText: t('Show path') } },
    MuiTablePagination: {
      defaultProps: {
        getItemAriaLabel: pageAriaLabel(t),
        labelRowsPerPage: t('Rows per page:'),
        labelDisplayedRows: ({ from, to, count }) =>
          count !== -1
            ? t('{{from}}–{{to}} of {{count}}', {
                from: number(from),
                to: number(to),
                count: number(count)
              })
            : t('{{from}}–{{to}} of more than {{to}}', {
                from: number(from),
                to: number(to)
              })
      }
    },
    MuiRating: {
      defaultProps: {
        getLabelText: (value) =>
          value !== 1
            ? t('{{value}} Stars', { value })
            : t('{{value}} Star', { value }),
        emptyLabelText: t('Empty')
      }
    },
    MuiAutocomplete: {
      defaultProps: {
        clearText: t('Clear'),
        closeText: t('Close'),
        loadingText: t('Loading…'),
        noOptionsText: t('No options'),
        openText: t('Open')
      }
    },
    MuiAlert: { defaultProps: { closeText: t('Close') } },
    MuiPagination: {
      defaultProps: {
        'aria-label': t('Pagination navigation'),
        getItemAriaLabel: (type, page, selected) => {
          if (type === 'page')
            return selected
              ? t('page {{page}}', { page })
              : t('Go to page {{page}}', { page });
          return pageAriaLabel(t)(type);
        }
      }
    },
    MuiDataGrid: { defaultProps: { localeText: gridLocaleText(t, lang) } },
    MuiLocalizationProvider: {
      defaultProps: { localeText: pickersLocaleText(t) }
    }
  };
  // Every string the overrides hold, and a sample of what each function says.
  const said = [];
  const collect = (value) => {
    if (typeof value === 'string') said.push(value);
    else if (value && typeof value === 'object')
      Object.values(value).forEach(collect);
  };
  collect(components);
  said.push(
    components.MuiDataGrid.defaultProps.localeText.paginationDisplayedRows({
      from: 1,
      to: 2,
      count: 3
    }),
    components.MuiDataGrid.defaultProps.localeText.footerRowSelected(2)
  );
  return { components, signature: `${lang}\u0000${said.join('\u0000')}` };
};

/**
 * `theme` with `overrides` merged into its components: each component's
 * defaultProps gain the localised text, its styleOverrides are kept.
 */
export const withMuiLocale = (theme, overrides) => {
  if (!overrides) return theme;
  const components = { ...(theme.components || {}) };
  for (const [name, entry] of Object.entries(overrides)) {
    components[name] = {
      ...components[name],
      defaultProps: {
        ...(components[name]?.defaultProps || {}),
        ...entry.defaultProps
      }
    };
  }
  return { ...theme, components };
};
